<template>
    <el-dialog
        v-model="visible"
        :title="mode === 'from' ? 'Select token to sell' : 'Select token to buy'"
        class="token-selector-modal"
        :class="{ 'is-mobile': isMobile }"
        :fullscreen="isMobile"
        :width="isMobile ? '100%' : '420px'"
        :show-close="true"
        :close-on-click-modal="true"
        :close-on-press-escape="true"
        @close="close"
    >
        <!-- Search Input -->
        <div class="search-container">
            <div class="search-input-wrapper">
                <svg class="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="M21 21l-4.35-4.35"></path>
                </svg>
                <input
                    ref="searchInput"
                    v-model="searchQuery"
                    type="text"
                    placeholder="Search by name or paste address"
                    class="search-input"
                    @input="onSearchInput"
                />
                <button v-if="searchQuery" class="clear-btn" @click="clearSearch">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                </button>
            </div>
        </div>

        <!-- Favorites Row -->
        <div class="favorites-section" v-if="!searchQuery">
            <div class="section-label">Favorites</div>
            <div class="favorites-row">
                <button
                    v-for="fav in favorites"
                    :key="fav.mint"
                    class="favorite-chip"
                    :class="{ disabled: isCurrentToken(fav.mint) }"
                    :disabled="isCurrentToken(fav.mint)"
                    @click="selectToken(fav)"
                >
                    <img v-if="fav.logoURI" :src="fav.logoURI" class="chip-logo" :alt="fav.symbol">
                    <span class="chip-symbol">{{ fav.symbol }}</span>
                </button>
            </div>
        </div>

        <!-- Token List -->
        <div class="token-list-container">
            <!-- Loading State -->
            <div v-if="isLoading" class="loading-state">
                <div class="loading-spinner"></div>
                <span>{{ searchQuery ? 'Searching...' : 'Loading popular tokens...' }}</span>
            </div>

            <!-- Search Results or Popular Tokens -->
            <div v-else class="token-list">
                <div class="section-label" v-if="!searchQuery">Popular Tokens</div>
                <div class="section-label" v-else>Search Results ({{ displayTokens.length }})</div>

                <div v-if="displayTokens.length === 0" class="empty-state">
                    <span v-if="searchQuery">No tokens found for "{{ searchQuery }}"</span>
                    <span v-else>No popular tokens available</span>
                </div>

                <button
                    v-for="token in displayTokens"
                    :key="token.mint"
                    class="token-row"
                    :class="{ disabled: isCurrentToken(token.mint) }"
                    :disabled="isCurrentToken(token.mint)"
                    @click="selectToken(token)"
                >
                    <div class="token-info">
                        <img
                            v-if="token.logoURI"
                            :src="token.logoURI"
                            class="token-logo"
                            :alt="token.symbol"
                            @error="handleImageError"
                        >
                        <div v-else class="token-logo-placeholder">
                            {{ token.symbol?.charAt(0) || '?' }}
                        </div>
                        <div class="token-details">
                            <span class="token-symbol">{{ token.symbol }}</span>
                            <span class="token-name">{{ token.name }}</span>
                        </div>
                    </div>
                    <div class="token-price-info" v-if="token.price_usd">
                        <span class="token-price">${{ formatPrice(token.price_usd) }}</span>
                        <span
                            class="token-change"
                            :class="token.change_24h_percent >= 0 ? 'positive' : 'negative'"
                        >
                            {{ token.change_24h_percent >= 0 ? '+' : '' }}{{ token.change_24h_percent?.toFixed(2) }}%
                        </span>
                    </div>
                </button>
            </div>
        </div>
    </el-dialog>
</template>

<script>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import axios from 'axios';
import debounce from 'lodash/debounce';

// Favorite token mint addresses - decimals fetched from API
const FAVORITE_MINTS = [
    'So11111111111111111111111111111111111111112',   // SOL
    'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v', // USDC
    'cbBTCn3W1TEHgF8RVCz5Q92dxzW6qMQnxsKvCz5Spump', // cbBTC
    'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263', // BONK
    'JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN'  // JUP
];

export default {
    name: 'TokenSelectorModal',

    props: {
        modelValue: {
            type: Boolean,
            default: false
        },
        mode: {
            type: String,
            default: 'from', // 'from' or 'to'
            validator: (v) => ['from', 'to'].includes(v)
        },
        currentFromToken: {
            type: String,
            default: null
        },
        currentToToken: {
            type: String,
            default: null
        }
    },

    emits: ['update:modelValue', 'select'],

    setup(props, { emit }) {
        const searchInput = ref(null);
        const searchQuery = ref('');
        const isLoading = ref(false);
        const popularTokens = ref([]);
        const searchResults = ref([]);
        const favorites = ref([]);
        const favoritesLoaded = ref(false);

        // Computed
        const visible = computed({
            get: () => props.modelValue,
            set: (val) => emit('update:modelValue', val)
        });

        const isMobile = computed(() => {
            return typeof window !== 'undefined' && window.innerWidth < 768;
        });

        const displayTokens = computed(() => {
            if (searchQuery.value) {
                return searchResults.value;
            }
            return popularTokens.value;
        });

        // Methods
        const close = () => {
            visible.value = false;
            searchQuery.value = '';
            searchResults.value = [];
        };

        const isCurrentToken = (mint) => {
            if (props.mode === 'from') {
                return mint === props.currentToToken;
            }
            return mint === props.currentFromToken;
        };

        const selectToken = (token) => {
            if (isCurrentToken(token.mint)) return;
            emit('select', { token, mode: props.mode });
            close();
        };

        const clearSearch = () => {
            searchQuery.value = '';
            searchResults.value = [];
        };

        const formatPrice = (price) => {
            if (!price || price === 0) return '0.00';
            if (price < 0.00001) return price.toExponential(2);
            if (price < 0.01) return price.toFixed(6);
            if (price < 1) return price.toFixed(4);
            if (price < 1000) return price.toFixed(2);
            return price.toLocaleString('en-US', { maximumFractionDigits: 2 });
        };

        const handleImageError = (e) => {
            e.target.style.display = 'none';
        };

        // Fetch favorite tokens from API (only once)
        const fetchFavorites = async () => {
            if (favoritesLoaded.value) return;

            try {
                // Fetch metadata for each favorite token by searching for its mint
                const fetchPromises = FAVORITE_MINTS.map(async (mint) => {
                    try {
                        const response = await axios.get('/api/tokens/search', {
                            params: { q: mint, limit: 1, enrich: true }
                        });
                        return response.data?.[0] || null;
                    } catch {
                        return null;
                    }
                });

                const results = await Promise.all(fetchPromises);
                favorites.value = results.filter(Boolean);
                favoritesLoaded.value = true;
            } catch (error) {
                console.error('Failed to fetch favorites:', error);
                favorites.value = [];
            }
        };

        // Fetch popular tokens
        const fetchPopularTokens = async () => {
            isLoading.value = true;
            try {
                const response = await axios.get('/api/tokens/popular', {
                    params: { limit: 15 }
                });
                popularTokens.value = response.data;
            } catch (error) {
                console.error('Failed to fetch popular tokens:', error);
                popularTokens.value = [];
            } finally {
                isLoading.value = false;
            }
        };

        // Search tokens (debounced)
        const searchTokens = debounce(async (query) => {
            if (!query || query.length < 2) {
                searchResults.value = [];
                isLoading.value = false;
                return;
            }

            isLoading.value = true;
            try {
                const response = await axios.get('/api/tokens/search', {
                    params: {
                        q: query,
                        enrich: true,
                        limit: 20
                    }
                });
                searchResults.value = response.data;
            } catch (error) {
                console.error('Token search failed:', error);
                searchResults.value = [];
            } finally {
                isLoading.value = false;
            }
        }, 300);

        const onSearchInput = () => {
            if (searchQuery.value) {
                isLoading.value = true;
                searchTokens(searchQuery.value);
            } else {
                searchResults.value = [];
            }
        };

        // Watch for modal open to fetch data and focus input
        watch(visible, async (isVisible) => {
            if (isVisible) {
                // Fetch favorites and popular tokens in parallel
                await Promise.all([
                    fetchFavorites(),
                    fetchPopularTokens()
                ]);
                await nextTick();
                searchInput.value?.focus();
            } else {
                searchQuery.value = '';
                searchResults.value = [];
            }
        });

        return {
            searchInput,
            searchQuery,
            isLoading,
            popularTokens,
            searchResults,
            favorites,
            visible,
            isMobile,
            displayTokens,
            close,
            isCurrentToken,
            selectToken,
            clearSearch,
            formatPrice,
            handleImageError,
            onSearchInput
        };
    }
};
</script>

<style scoped>
/* Modal styling is handled in _compass-design.scss for theme consistency */
.token-selector-modal {
    --modal-bg: #1a1a2e;
    --modal-border: rgba(139, 92, 246, 0.2);
    --input-bg: rgba(0, 0, 0, 0.3);
    --text-primary: #e6edf3;
    --text-secondary: #9ca3af;
    --text-muted: #6b7280;
    --accent: #8b5cf6;
    --accent-hover: rgba(139, 92, 246, 0.2);
    --positive: #22c55e;
    --negative: #ef4444;
}

.search-container {
    margin-bottom: 1rem;
}

.search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.search-icon {
    position: absolute;
    left: 12px;
    color: var(--text-muted);
    pointer-events: none;
}

.search-input {
    width: 100%;
    padding: 0.75rem 2.5rem 0.75rem 2.5rem;
    background: var(--input-bg);
    border: 1px solid var(--modal-border);
    border-radius: 10px;
    color: var(--text-primary);
    font-size: 0.95rem;
    outline: none;
    transition: border-color 0.2s;
}

.search-input:focus {
    border-color: var(--accent);
}

.search-input::placeholder {
    color: var(--text-muted);
}

.clear-btn {
    position: absolute;
    right: 10px;
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.clear-btn:hover {
    color: var(--text-primary);
}

/* Favorites Section */
.favorites-section {
    margin-bottom: 1rem;
}

.section-label {
    font-size: 0.7rem;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.5rem;
}

.favorites-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.favorite-chip {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.4rem 0.75rem;
    background: var(--accent-hover);
    border: 1px solid var(--modal-border);
    border-radius: 20px;
    color: var(--text-primary);
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.favorite-chip:hover:not(.disabled) {
    border-color: var(--accent);
    background: rgba(139, 92, 246, 0.3);
}

.favorite-chip.disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.chip-logo {
    width: 18px;
    height: 18px;
    border-radius: 50%;
}

.chip-symbol {
    font-weight: 600;
}

/* Token List */
.token-list-container {
    max-height: 400px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: var(--accent) transparent;
}

.token-list-container::-webkit-scrollbar {
    width: 6px;
}

.token-list-container::-webkit-scrollbar-thumb {
    background: var(--accent);
    border-radius: 3px;
}

.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    color: var(--text-secondary);
    gap: 0.75rem;
}

.loading-spinner {
    width: 24px;
    height: 24px;
    border: 2px solid rgba(139, 92, 246, 0.3);
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.empty-state {
    text-align: center;
    padding: 2rem;
    color: var(--text-muted);
}

.token-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.75rem;
    background: transparent;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.2s;
    text-align: left;
}

.token-row:hover:not(.disabled) {
    background: var(--accent-hover);
}

.token-row.disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.token-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.token-logo {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
}

.token-logo-placeholder {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--accent), #ec4899);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1rem;
    color: white;
}

.token-details {
    display: flex;
    flex-direction: column;
}

.token-symbol {
    font-weight: 600;
    color: var(--text-primary);
    font-size: 0.95rem;
}

.token-name {
    font-size: 0.75rem;
    color: var(--text-muted);
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.token-price-info {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

.token-price {
    font-weight: 600;
    color: var(--text-primary);
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.85rem;
}

.token-change {
    font-size: 0.75rem;
    font-family: 'JetBrains Mono', monospace;
}

.token-change.positive {
    color: var(--positive);
}

.token-change.negative {
    color: var(--negative);
}

/* Mobile fullscreen adjustments */
.is-mobile .token-list-container {
    max-height: calc(100vh - 200px);
}
</style>
